/**
 * filterAndSortProducts — extraído de ForecastPage.tsx (P7 split).
 *
 * Aplica al array de productos:
 *   1) Filtros: dual search bar (include + exclude prefijo `-`),
 *      inactiveViewMode, dciFilter, categoría (prefix match), hideDeselected.
 *   2) Sort por SortField + SortOrder.
 *
 * Sintaxis de búsqueda: la coma separa alternativas (O) y `+` dentro de una
 * alternativa exige todos sus términos (Y). `-término` excluye.
 *   "wl imp + navid, esfera" → (contiene "wl imp" Y "navid") O contiene "esfera".
 *
 * Pure function. La página la envuelve en useMemo.
 */

import { ProductData, SortField, SortOrder } from "./types";
import {
  AdvancedFilters,
  QuickFilterValue,
  applyQuickAndAdvancedFilters,
} from "./selectors";

export type InactiveViewMode = "hidden" | "onlyInactive" | "show" | "all";

export interface FilterAndSortOptions {
  products: ProductData[];
  debouncedSearchTerm: string;
  debouncedSecondSearchTerm: string;
  searchIndex: Record<string, string>;
  sortNameMap: Record<string, string>;
  inactiveViewMode: InactiveViewMode;
  dciFilterActive: boolean;
  categoryFilter: string;
  hideDeselected: boolean;
  selectedIds: Set<string>;
  sortField: SortField;
  sortOrder: SortOrder;
  getDisplayText: (
    name: string | { en_US: string; es_MX: string },
  ) => string;
  // Nuevo (handoff Fase 2 — opcional para preservar compat con callers legacy)
  quickFilter?: QuickFilterValue;
  advancedFilters?: AdvancedFilters;
}

export function applySearchTerm(
  filtered: ProductData[],
  term: string,
  searchIndex: Record<string, string>,
): ProductData[] {
  if (!term) return filtered;
  const raw = term
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const include = raw
    .filter((t) => !t.startsWith("-"))
    .map((clause) =>
      clause
        .split("+")
        .map((part) => part.trim())
        .filter(Boolean),
    )
    .filter((parts) => parts.length > 0);
  const exclude = raw
    .filter((t) => t.startsWith("-"))
    .map((t) => t.slice(1).trim())
    .filter(Boolean);
  if (include.length === 0 && exclude.length === 0) return filtered;
  return filtered.filter((product) => {
    const idx = searchIndex[product.unifiedProductId] ?? "";
    const matchesInclude =
      include.length === 0 ||
      include.some((parts) => parts.every((q) => idx.includes(q)));
    const matchesExclude =
      exclude.length > 0 && exclude.some((q) => idx.includes(q));
    return matchesInclude && !matchesExclude;
  });
}

export function filterAndSortProducts(
  opts: FilterAndSortOptions,
): ProductData[] {
  const {
    products,
    debouncedSearchTerm,
    debouncedSecondSearchTerm,
    searchIndex,
    sortNameMap,
    inactiveViewMode,
    dciFilterActive,
    categoryFilter,
    hideDeselected,
    selectedIds,
    sortField,
    sortOrder,
    getDisplayText,
    quickFilter,
    advancedFilters,
  } = opts;

  let filtered = [...products];

  // Quick filter + advanced filters (handoff Fase 2 + Fase 6)
  if (quickFilter || advancedFilters) {
    filtered = applyQuickAndAdvancedFilters(
      filtered,
      quickFilter ?? "all",
      advancedFilters ?? {
        abc: new Set<string>(),
        xyz: new Set<string>(),
        demand: new Set<string>(),
        lifecycle: new Set<string>(),
        dci: new Set<string>(),
        activeOnly: false,
      },
    );
  }

  // Búsqueda primaria
  filtered = applySearchTerm(filtered, debouncedSearchTerm, searchIndex);
  // Búsqueda secundaria en cascada
  filtered = applySearchTerm(filtered, debouncedSecondSearchTerm, searchIndex);

  // Filtro por estado de compras
  if (inactiveViewMode === "hidden") {
    filtered = filtered.filter((p) => p.activeForPurchase !== false);
  } else if (inactiveViewMode === "onlyInactive") {
    filtered = filtered.filter((p) => p.activeForPurchase === false);
  }

  // Filtro DCI
  if (dciFilterActive) {
    filtered = filtered.filter((p) => p.dciScore != null && p.dciScore < 0.5);
  }

  // Filtro por categoría (prefix match para nodos padre)
  if (categoryFilter) {
    if (categoryFilter === "Sin clasificar") {
      filtered = filtered.filter(
        (p) => !p.unifiedCategory || p.unifiedCategory.trim() === "",
      );
    } else {
      filtered = filtered.filter(
        (p) =>
          p.unifiedCategory === categoryFilter ||
          p.unifiedCategory?.startsWith(categoryFilter + " / "),
      );
    }
  }

  // Ocultar deseleccionados
  if (hideDeselected) {
    filtered = filtered.filter((p) => selectedIds.has(p.unifiedProductId));
  }

  // Sort
  filtered.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortField) {
      case "unifiedName":
        aValue =
          sortNameMap[a.unifiedProductId] ?? getDisplayText(a.unifiedName);
        bValue =
          sortNameMap[b.unifiedProductId] ?? getDisplayText(b.unifiedName);
        break;
      case "unifiedCode":
        aValue = a.unifiedCode || "";
        bValue = b.unifiedCode || "";
        break;
      case "unifiedCategory":
        aValue = a.unifiedCategory || "";
        bValue = b.unifiedCategory || "";
        break;
      case "matchingMethod":
        aValue = a.matchingMethod;
        bValue = b.matchingMethod;
        break;
      case "matchingConfidence":
        aValue = a.matchingConfidence;
        bValue = b.matchingConfidence;
        break;
      case "totalAvailableQty":
        aValue = a.totalAvailableQty ?? 0;
        bValue = b.totalAvailableQty ?? 0;
        break;
      case "recentSalesAmount":
        aValue = a.recentSalesAmount ?? 0;
        bValue = b.recentSalesAmount ?? 0;
        break;
      case "dciScore":
        aValue = a.dciScore ?? -1;
        bValue = b.dciScore ?? -1;
        break;
      case "demandClassification":
        aValue = a.demandClassification || "";
        bValue = b.demandClassification || "";
        break;
      case "abcClassification":
        aValue = a.abcClassification || "";
        bValue = b.abcClassification || "";
        break;
      case "abcXyzSegment":
        aValue = a.abcXyzSegment || "";
        bValue = b.abcXyzSegment || "";
        break;
      case "safetyStock":
        aValue = a.safetyStock ?? -1;
        bValue = b.safetyStock ?? -1;
        break;
      case "reorderPoint":
        aValue = a.reorderPoint ?? -1;
        bValue = b.reorderPoint ?? -1;
        break;
      case "forecastNextPeriod":
        aValue = a.forecastNextPeriod ?? -1;
        bValue = b.forecastNextPeriod ?? -1;
        break;
      default:
        aValue = "";
        bValue = "";
    }

    if (sortOrder === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return filtered;
}
